import React, { useEffect, useMemo } from 'react'
import { ToastContainer } from 'react-toastify'
import { useAtomValue } from 'jotai/index'
import { useLocation } from 'react-router-dom'

import AppRoutes from './routes/AppRoutes'
import ScrollToTop from './components/ScrollToTop/ScrollToTop'
import { AuthProvider } from './contexts/AuthContext'
import 'react-toastify/dist/ReactToastify.css'
import { staffAreaDesktopToastContainerOffsetStyle, toastConfig } from './config/toastify.config'
import { bookingIdAtom } from './features/BeachPlan/jotai-modules/atoms'
import { useIsDesktop } from './hooks/useScreenSize'
import { isStaffAreaPathname } from './utils/staffAreaRoute.utils'
import { getStaffPendingCheckoutIdForUnload } from './utils/pendingCheckoutSession'

const App: React.FC = () => {
  const bookingId = useAtomValue(bookingIdAtom)
  const { pathname } = useLocation()
  const isDesktop = useIsDesktop()

  const toastContainerProps = useMemo(
    () => ({
      ...toastConfig,
      ...(isStaffAreaPathname(pathname) && isDesktop ? { style: staffAreaDesktopToastContainerOffsetStyle } : {}),
    }),
    [pathname, isDesktop]
  )

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const pendingId = bookingId.trim() || getStaffPendingCheckoutIdForUnload() || ''
      if (!pendingId) {
        return
      }

      // Only show the browser prompt — do not delete or clear state here. beforeunload runs
      // when the user opens the reload dialog, including if they click Cancel. Confirmed
      // reload is handled by useStaffPendingCheckoutMountCleanup (session_mount).
      event.preventDefault()
      event.returnValue = 'Are you sure you want to leave? Your data might be lost.'
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [bookingId])

  return (
    <>
      <ToastContainer {...toastContainerProps} />
      <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
      </AuthProvider>
    </>
  )
}

export default App
